import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import type React from 'react';
import { Link } from 'react-router-dom';
import { buttonColorLink, type ColorKeys, cardColorClass, cardIconColorClass, iconColorClass } from './utils';

export interface VehicleCardItemProps {
  title: string;
  tooltip: string;
  subtitle?: string;
  icon: IconNames;
  color: ColorKeys;
  disabled?: boolean;
  itemLink?: string;
  pageLink?: string;
  onLinkClick?: () => void;
}

const VehicleCardItem = ({ title, tooltip, subtitle, icon, color, disabled, itemLink, pageLink, onLinkClick }: VehicleCardItemProps) => {
  const cardColor = cardColorClass(color);
  const cardIconColor = cardIconColorClass(color);
  const iconColor = iconColorClass(color);
  const btnColor = buttonColorLink(color);

  const LinkComponent = ({ children }: { children: React.ReactNode }) => {
    if (!pageLink && disabled) return <div>{children}</div>;
    return (
      <Link to={disabled ? pageLink || '' : itemLink || ''} onClick={onLinkClick}>
        {children}
      </Link>
    );
  };

  if (disabled) {
    return (
      <section className={cn('flex h-11 flex-col gap-2 rounded-md border border-gray-3', pageLink && 'hover:border-gray-4')}>
        <div className={cn('group flex flex-col rounded-md bg-gray-2 p-1')}>
          <div className='flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              <Tooltip content={tooltip} asChild>
                <div className={cn('rounded-md border border-gray-4 bg-gray-3 p-2')}>
                  <Icon name={icon} color='gray' className='size-4' />
                </div>
              </Tooltip>
              <div>
                <p className='font-medium text-sm'>{title}</p>
              </div>
            </div>
            {pageLink && (
              <Link to={pageLink} className='mr-2 items-center gap-2 opacity-0 duration-200 group-hover:opacity-100' onClick={onLinkClick}>
                <Button variant='ghost' size='sm' color='default' className={cn('!size-6')} icon={<Icon name='square-arrow-out-up-right' className={cn('!size-3', iconColor)} />} />
              </Link>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={cn('group flex flex-col rounded-md border p-1', cardColor)}>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <Tooltip content={tooltip} asChild>
            <div className={cn('rounded-md border p-2', cardIconColor)}>
              <Icon name={icon} className={cn('size-4', iconColor)} />
            </div>
          </Tooltip>
          <div>
            <p className='font-medium text-sm'>{title}</p>
            {subtitle && <p className='text-xs'>{subtitle}</p>}
          </div>
        </div>
        <div className='mr-2 items-center gap-2 opacity-0 group-hover:opacity-100'>
          <LinkComponent>
            <Button
              variant='ghost'
              size='sm'
              color={btnColor as any}
              className={cn('!size-6', iconColor)}
              icon={<Icon name='square-arrow-out-up-right' className={cn('!size-3', iconColor)} />}
            />
          </LinkComponent>
        </div>
      </div>
    </section>
  );
};

export default VehicleCardItem;
